import React from 'react';
import { Head } from 'vite-react-ssg';

/**
 * Per-page document head.
 *
 * IMPORTANT: this renders through vite-react-ssg's <Head> (a react-helmet-async
 * wrapper) so the tags are emitted at STATIC BUILD TIME into each route's HTML.
 * The previous implementation set these tags imperatively in a useEffect, which
 * only ran in a live browser after hydration — so crawlers (Google, and the AI
 * search bots that read its index) saw index.html's homepage title + a canonical
 * pointing at the homepage on EVERY page, collapsing the whole site to one URL.
 * Do not move head management back into an effect.
 */
interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  article?: {
    publishedTime: string;
    author: string;
    tags?: string[];
  };
  structuredData?: object | object[];
}

const BRAND_SUFFIX = 'Meshly — The AI verification layer';

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage = '/og.png',
  article,
  structuredData,
}) => {
  // Some pages already carry the brand in their title ("Pricing | Meshly …").
  // Only append the suffix when it isn't already there, so we never render a
  // doubled "… | Meshly | Meshly …".
  const fullTitle = /meshly/i.test(title) ? title : `${title} | ${BRAND_SUFFIX}`;
  const absoluteImage = ogImage.startsWith('http')
    ? ogImage
    : `https://www.meshly.ai${ogImage}`;
  const schemas = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  // Build the tag list explicitly (rather than inline `&&`) so no `false`
  // children ever reach react-helmet-async.
  const tags: React.ReactNode[] = [
    <title key="title">{fullTitle}</title>,
    <meta key="description" name="description" content={description} />,
    <meta key="og:title" property="og:title" content={fullTitle} />,
    <meta key="og:description" property="og:description" content={description} />,
    <meta key="og:image" property="og:image" content={absoluteImage} />,
    <meta key="og:type" property="og:type" content={article ? 'article' : 'website'} />,
    <meta key="og:site_name" property="og:site_name" content="Meshly" />,
    <meta key="tw:card" name="twitter:card" content="summary_large_image" />,
    <meta key="tw:title" name="twitter:title" content={fullTitle} />,
    <meta key="tw:description" name="twitter:description" content={description} />,
    <meta key="tw:image" name="twitter:image" content={absoluteImage} />,
  ];

  if (canonicalUrl) {
    tags.push(
      <link key="canonical" rel="canonical" href={canonicalUrl} />,
      <meta key="og:url" property="og:url" content={canonicalUrl} />,
    );
  }

  if (article) {
    tags.push(
      <meta
        key="article:published"
        property="article:published_time"
        content={article.publishedTime}
      />,
      <meta key="article:author" property="article:author" content={article.author} />,
    );
    article.tags?.forEach((tag) =>
      tags.push(
        <meta key={`article:tag:${tag}`} property="article:tag" content={tag} />,
      ),
    );
  }

  schemas.forEach((schema, i) =>
    tags.push(
      <script key={`ld:${i}`} type="application/ld+json">
        {JSON.stringify(schema)}
      </script>,
    ),
  );

  return <Head>{tags}</Head>;
};
